export type FirmNameSource = {
  name?: string | null;
  firmName?: string | null;
  shortName?: string | null;
  firmShortName?: string | null;
  firmLongName?: string | null;
  firmLegalName?: string | null;
  longName?: string | null;
};

function text(value: unknown): string {
  return String(value ?? "").trim();
}

/** `Firm.name`. */
export function firmLongName(firm?: FirmNameSource | null): string {
  return text(firm?.firmLongName) || text(firm?.longName) || text(firm?.name);
}

/** `Firm.firmName`, or the office copy `firmLegalName`. */
export function firmLegalName(firm?: FirmNameSource | null): string {
  return text(firm?.firmLegalName) || text(firm?.firmName);
}

/** Staff label. Falls back to the long name when short name is not set yet. */
export function firmShortLabel(firm?: FirmNameSource | null): string {
  const shortName = text(firm?.shortName) || text(firm?.firmShortName);
  if (shortName) {
    return shortName;
  }
  return firmLongName(firm);
}

/** Public member search shows the long name. */
export function firmMemberSearchLabel(firm?: FirmNameSource | null): string {
  return firmLongName(firm);
}

/** Staff routes show the short name. Member, public, and data admin show the long name. */
export function firmAudienceName(
  firm: FirmNameSource | null | undefined,
  staffSurface: boolean,
): string {
  if (staffSurface) {
    return firmShortLabel(firm);
  }
  return firmLongName(firm) || firmLegalName(firm);
}

export type FirmShortLabelIndex = {
  byId: Map<string, string>;
  byText: Map<string, string>;
};

/** Maps firm ids and known name variants to the short label. */
export function buildFirmShortLabelIndex(
  firms: Array<FirmNameSource & { id?: string | null }>,
): FirmShortLabelIndex {
  const byId = new Map<string, string>();
  const byText = new Map<string, string>();
  for (const firm of firms) {
    const label = firmShortLabel(firm);
    if (!label) {
      continue;
    }
    const id = text(firm.id);
    if (id) {
      byId.set(id, label);
    }
    const variants = [
      label,
      firm.shortName,
      firm.firmShortName,
      firm.name,
      firm.longName,
      firm.firmLongName,
      firm.firmName,
      firm.firmLegalName,
    ];
    for (const variant of variants) {
      const key = text(variant).toLowerCase();
      if (key && !byText.has(key)) {
        byText.set(key, label);
      }
    }
  }
  return { byId, byText };
}

export function lookupFirmShortLabel(
  index: FirmShortLabelIndex,
  value: unknown,
  firmId?: unknown,
): string {
  const id = text(firmId);
  if (id) {
    const byId = index.byId.get(id);
    if (byId) {
      return byId;
    }
  }
  const raw = text(value);
  if (!raw) {
    return "";
  }
  return index.byText.get(raw.toLowerCase()) ?? raw;
}

/** Name to persist. Keeps the long name when a staff list is showing the short label in `name`. */
export function firmStoredLongName(firm?: FirmNameSource | null): string {
  return text(firm?.longName) || text(firm?.firmLongName) || text(firm?.name);
}
